import React, { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Info, MapPinOff, XCircle } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

// Phase 161 — Zone / Ward / Mohalla linkage flags raised while an upload is previewed. The backend attaches
// `area_flags` to every preview row (code, level, label, message, ward, given_zone, master_zone, mohalla, exists_in)
// and a roll-up ({rows, counts, groups[]}) to the response. The banner shows the grouped view; <AreaFlagBadge />
// marks the individual Ward / Mohalla cells in the preview table.

const n = (v) => Number(v || 0).toLocaleString("en-IN");

export const FLAG_TONE = {
  error: { cls: "bg-red-50 text-red-800 border-red-200", Icon: XCircle },
  warn: { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  info: { cls: "bg-sky-50 text-sky-800 border-sky-200", Icon: Info },
};

export const WARD_FLAGS = new Set(["ward_zone_mismatch", "ward_ambiguous", "ward_not_found", "ward_will_be_created"]);
export const MOHALLA_FLAGS = new Set(["mohalla_not_in_ward", "mohalla_ambiguous", "mohalla_will_be_created"]);

/** The strongest flag (error > warn > info) for a cell: `cell` = "ward" | "mohalla". */
export function pickFlag(flags, cell) {
  const set = cell === "ward" ? WARD_FLAGS : MOHALLA_FLAGS;
  const mine = (flags || []).filter((f) => set.has(f.code));
  if (!mine.length) return null;
  const order = { error: 0, warn: 1, info: 2 };
  return mine.sort((a, b) => (order[a.level] ?? 9) - (order[b.level] ?? 9))[0];
}

/** Small badge rendered under a Ward / Mohalla value in the preview table. */
export function AreaFlagBadge({ flag, rowNumber, cell }) {
  if (!flag) return null;
  const tone = FLAG_TONE[flag.level] || FLAG_TONE.warn;
  const Icon = tone.Icon;
  return (
    <Badge variant="outline" title={flag.message}
      className={`mt-0.5 max-w-[11rem] gap-1 text-[10px] font-normal leading-tight whitespace-normal ${tone.cls}`}
      data-testid={`upload-preview-flag-${cell}-${rowNumber}`} data-flag={flag.code}>
      <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{shortLabel(flag)}</span>
    </Badge>
  );
}

function shortLabel(f) {
  switch (f.code) {
    case "ward_zone_mismatch": return `In ${f.master_zone || "another zone"}, not ${f.given_zone || "given zone"}`;
    case "ward_ambiguous": return "Ward in several zones";
    case "ward_not_found": return "Ward not found";
    case "ward_will_be_created": return "New ward";
    case "mohalla_not_in_ward": return f.exists_in?.length ? `Not in ${f.ward || "ward"} · under ${f.exists_in[0]}` : `Not in ${f.ward || "this ward"}`;
    case "mohalla_ambiguous": return "Duplicate mohalla names";
    case "mohalla_will_be_created": return f.exists_in?.length ? `New here · exists under ${f.exists_in[0]}` : "New mohalla";
    default: return f.label;
  }
}

/**
 * Grouped banner above the preview table.
 *   summary  {rows, counts, groups[]} from the backend
 *   total    total rows in the file (for the "x of y rows" line)
 *   onFilter optional: called with a flag code when the admin clicks "Show rows" on a group
 */
export function AreaFlagsBanner({ summary, total, onFilter, testId = "area-flags-banner" }) {
  const [open, setOpen] = useState(true);
  if (!summary || !summary.rows) return null;
  const groups = summary.groups || [];
  const worst = groups.some((g) => g.level === "error") ? "error" : groups.some((g) => g.level === "warn") ? "warn" : "info";
  const tone = FLAG_TONE[worst];
  const counts = summary.counts || {};
  const mismatch = (counts.ward_zone_mismatch || 0) + (counts.ward_ambiguous || 0) + (counts.ward_not_found || 0);
  const mohalla = (counts.mohalla_not_in_ward || 0) + (counts.mohalla_ambiguous || 0) + (counts.mohalla_will_be_created || 0);
  return (
    <div className={`rounded-lg border p-3 ${tone.cls}`} data-testid={testId} data-level={worst}>
      <div className="flex flex-wrap items-start gap-2">
        <MapPinOff className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium" data-testid={`${testId}-title`}>
            Area check: {n(summary.rows)} of {n(total)} row{total === 1 ? "" : "s"} flagged
            {mismatch ? ` · ${n(mismatch)} ward/zone` : ""}{mohalla ? ` · ${n(mohalla)} mohalla/ward` : ""}
          </p>
          <p className="text-xs opacity-90 mt-0.5">
            The zone, ward and mohalla written in the file are matched inside each other (ward inside its zone, mohalla inside its ward).
            Flagged rows are still imported as described below — fix the file or Master Data first if that is not what you want.
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs shrink-0 hover:bg-background/60" onClick={() => setOpen((v) => !v)}
          data-testid={`${testId}-toggle`}>
          {open ? <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Hide</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Details</>}
        </Button>
      </div>
      {open && (
        <ul className="mt-2 divide-y divide-current/10 rounded-md border border-current/15 bg-background/70" data-testid={`${testId}-groups`}>
          {groups.slice(0, 12).map((g, idx) => {
            const t = FLAG_TONE[g.level] || FLAG_TONE.warn;
            const Icon = t.Icon;
            return (
              <li key={idx} className="flex items-start gap-2 px-2.5 py-1.5 text-xs text-foreground" data-testid={`${testId}-group-${g.code}-${idx}`}>
                <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${g.level === "error" ? "text-red-600" : g.level === "warn" ? "text-amber-600" : "text-sky-600"}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="leading-snug"><span className="font-medium">{g.label}</span> — {g.message}</p>
                  <p className="text-[11px] text-muted-foreground">
                    <span className="tabular-nums font-medium text-foreground">{n(g.count)}</span> row{g.count === 1 ? "" : "s"}
                    {g.sample_rows?.length ? ` · e.g. row ${g.sample_rows.slice(0, 4).join(", ")}` : ""}
                  </p>
                </div>
                {onFilter && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] shrink-0" onClick={() => onFilter(g.code)}
                    data-testid={`${testId}-show-${g.code}-${idx}`}>Show rows</Button>
                )}
              </li>
            );
          })}
          {groups.length > 12 && <li className="px-2.5 py-1.5 text-[11px] text-muted-foreground">+{groups.length - 12} more groups</li>}
        </ul>
      )}
    </div>
  );
}
